import React, {useEffect, useRef, useState} from 'react';
import {AppBar, Grid, makeStyles, Toolbar} from '@material-ui/core';
import RhymeForm from './RhymeForm';
import RhymeList from './RhymeList';
import {getRhymes} from './api';
import languages, {LANGUAGE_ALIASES} from "./constants";
import StoreLinks from "./StoreLinks";
import AdBanner from "./AdBanner";
import {DEFAULT_RHYME_TYPE, getSections, isRhymeType, isRhymeTypeAvailable} from "./rhymeTypes";
import {applyMeta, pageMeta} from "./seo";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    title: {
        flexGrow: 1,
    },
    logo: {
        height: '100px',
        maxWidth: '100%',
        objectFit: 'contain',
        [theme.breakpoints.down('xs')]: {
            height: '72px',
        },
    },
    content: {
        // Side gutter on phones; the centred column handles wider screens.
        padding: theme.spacing(0, 2, 2),
    },
    contentWithBanner: {
        // Keep the last results visible above the fixed ad banner.
        paddingBottom: theme.spacing(12),
    },
    visuallyHidden: {
        position: 'absolute',
        width: 1,
        height: 1,
        margin: -1,
        padding: 0,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: 0,
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'center',
    },
}));


function App() {
    const classes = useStyles();
    const [word, setWord] = useState(get_default_word());
    const [language, setLanguage] = useState(get_default_language());
    const [rhymeType, setRhymeType] = useState(() => get_default_rhyme_type(language));
    // The response together with the rhyme type it was requested for, so the
    // bucket mapping always matches the data being rendered.
    const [rhymes, setRhymes] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    // Term, language and type of the search currently shown (or loading), for
    // the page title / canonical / robots tags.
    const [shownSearch, setShownSearch] = useState(null);
    const [showAdBanner, setShowAdBanner] = useState(false);
    const [adDismissed, setAdDismissed] = useState(false);

    // Monotonic request id. Without it a slow response for an earlier query
    // could land after a faster later one and overwrite the newer results.
    const requestIdRef = useRef(0);

    // Check if ad was previously dismissed
    useEffect(() => {
        const dismissed = localStorage.getItem('adBannerDismissed');
        if (dismissed === 'true') {
            setAdDismissed(true);
        }
    }, []);

    const handleWordChange = (event) => {
        setWord(event.target.value);
    };
    // Re-run the search whenever the language or rhyme type changes (and once
    // on mount, which is what restores a shared ?s=...&hl=...&t=... link).
    useEffect(() => {
        handleGetRhymes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, rhymeType]);


    const handleLanguageChange = (lang) => {
        setLanguage(lang);
        // Proverbs exist only for a few languages; fall back to double rhymes
        // instead of showing a tab that can only ever be empty.
        if (!isRhymeTypeAvailable(rhymeType, lang)) {
            setRhymeType(DEFAULT_RHYME_TYPE);
        }

    };

    const handleRhymeTypeChange = (type) => {
        setRhymeType(type);
    };

    function updateUrlParams() {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('hl', language);
        urlParams.set('s', word);
        urlParams.set('t', rhymeType);
        window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
    }

    const handleGetRhymes = () => {
        const trimmedWord = word.trim();
        if (trimmedWord === '') {
            return;
        }
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);
        setHasSearched(true);
        const requestedType = rhymeType;
        setShownSearch({word: trimmedWord, language, rhymeType: requestedType});
        getRhymes(trimmedWord, language, requestedType)
            .then(response => {
                if (requestId !== requestIdRef.current) {
                    return; // a newer search has already been issued
                }
                setRhymes({response, rhymeType: requestedType});
                setIsLoading(false);

                // Show ad banner after successful rhyme search (if not dismissed)
                if (!adDismissed && response && response.typeToResultMap) {
                    const hasResults = Object.values(response.typeToResultMap).some(arr => arr && arr.length > 0);
                    if (hasResults) {
                        setTimeout(() => {
                            setShowAdBanner(true);
                        }, 2000); // Show after 2 seconds to let user see results first
                    }
                }
            })
            .catch(err => {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                console.error(err);
                setRhymes(undefined);
                setError(err.message);
                setIsLoading(false);
            });
        updateUrlParams();
    };

    // Title, description, canonical and robots of the current page.
    useEffect(() => {
        if (!shownSearch) {
            applyMeta(pageMeta({state: 'home'}));
            return;
        }
        let state = 'loading';
        let sample = [];
        if (!isLoading) {
            if (error || !rhymes || !rhymes.response || !rhymes.response.typeToResultMap) {
                state = 'error';
            } else {
                const sections = getSections(rhymes.response.typeToResultMap, rhymes.rhymeType);
                sample = sections.flatMap(section => section.items);
                state = sample.length > 0 ? 'results' : 'empty';
            }
        }
        applyMeta(pageMeta({...shownSearch, state, sample}));
    }, [shownSearch, rhymes, isLoading, error]);

    const handleAdBannerClose = () => {
        setShowAdBanner(false);
        setAdDismissed(true);
        localStorage.setItem('adBannerDismissed', 'true');
    };

    const handleAdBannerClick = () => {
        // Open InstantDate app on Google Play Store
        window.open('https://play.google.com/store/apps/details?id=com.lyricspiration.instantdate', '_blank', 'noopener,noreferrer');
        
        // Optional: track click analytics here
        console.log('InstantDate ad banner clicked');
    };

    return (
        <div className={classes.root}>
            <AppBar position="static">
                <Toolbar className={classes.toolbar}>
                    <img src="/logo_en_white2.png" alt="Double Rhyme" className={classes.logo}/>
                </Toolbar>
            </AppBar>
            <main className={`${classes.content} ${showAdBanner && !adDismissed ? classes.contentWithBanner : ''}`}>
            <h1 className={classes.visuallyHidden}>Double Rhyme: find multi-syllable rhymes</h1>
            <Grid container justifyContent="center">
                <Grid item xs={12} sm={10} md={8} lg={6}>
                    <StoreLinks/>
                    <RhymeForm
                        word={word}
                        language={language}
                        rhymeType={rhymeType}
                        onWordChange={handleWordChange}
                        onLanguageChange={handleLanguageChange}
                        onRhymeTypeChange={handleRhymeTypeChange}
                        onGetRhymes={handleGetRhymes}
                        isLoading={isLoading}
                    />
                    <div
                        id="rhyme-results"
                        role="tabpanel"
                        aria-labelledby={`rhyme-type-tab-${rhymeType}`}
                        aria-busy={isLoading}
                    >
                        <RhymeList
                            rhymes={rhymes && rhymes.response}
                            rhymeType={rhymes ? rhymes.rhymeType : rhymeType}
                            isLoading={isLoading}
                            error={error}
                            hasSearched={hasSearched}
                        />
                    </div>
                </Grid>
            </Grid>
            </main>
            
            {showAdBanner && !adDismissed && (
                <AdBanner 
                    onClose={handleAdBannerClose}
                    onClick={handleAdBannerClick}
                />
            )}
        </div>
    );
}


export function parseLangCode(rawLangCode) {
    if (!rawLangCode) {
        return undefined;
    }

    if (LANGUAGE_ALIASES[rawLangCode]) {
        return LANGUAGE_ALIASES[rawLangCode];
    }
    // Exact ids first (es_lat, pt_br, fr_be, ...), then the primary subtag of
    // a browser locale such as "de-AT".
    if (languages.some(lang => lang.code === rawLangCode)) {
        return rawLangCode;
    }
    const langFirstPart = rawLangCode.split('-')[0];
    for (let i = 0; i < languages.length; i++) {
        if (languages[i].code === langFirstPart) {
            return langFirstPart;
        }
    }
    return undefined;
}

function get_default_language() {
    // get language from the url params
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('hl');

    // get language from browser
    const browserLang = navigator.language;

    const langList = [urlLang, browserLang];
    for (let i = 0; i < langList.length; i++) {
        const parsedLangCode = parseLangCode(langList[i]);
        if (parsedLangCode) {
            return parsedLangCode;
        }
    }
    // default to English
    return "en";
}

function get_default_rhyme_type(language) {
    const urlType = new URLSearchParams(window.location.search).get('t');
    return isRhymeType(urlType) && isRhymeTypeAvailable(urlType, language) ? urlType : DEFAULT_RHYME_TYPE;
}

function get_default_word() {
    // get word from the url params
    const urlParams = new URLSearchParams(window.location.search);
    const urlWord = urlParams.get('s');

    return urlWord || '';
}


export default App;
